import { useState } from "react";
import { useAdminList, AdminRow } from "@/hooks/useAdminList";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ChevronLeft, ChevronRight, Plus, Pencil, Trash2, Mail, User, KeyRound, Clock, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

export function AdminManagement() {
  const {
    admins, loading, total, page, setPage, pageSize, setPageSize, totalPages,
    refetch, updateRole, updateProfile, deleteAdmin,
  } = useAdminList();

  const [editDialog, setEditDialog] = useState<AdminRow | null>(null);
  const [editRole, setEditRole] = useState("");
  const [editFirstName, setEditFirstName] = useState("");
  const [editLastName, setEditLastName] = useState("");
  const [deleteDialog, setDeleteDialog] = useState<AdminRow | null>(null);
  const [inviteDialog, setInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteRole, setInviteRole] = useState("admin");
  const [inviteFirstName, setInviteFirstName] = useState("");
  const [inviteLastName, setInviteLastName] = useState("");
  const [inviting, setInviting] = useState(false);

  const handleEditOpen = (admin: AdminRow) => {
    setEditDialog(admin);
    setEditRole(admin.role);
    setEditFirstName(admin.first_name ?? "");
    setEditLastName(admin.last_name ?? "");
  };

  const handleEditSave = async () => {
    if (!editDialog) return;
    const roleChanged = editRole !== editDialog.role;
    const nameChanged =
      editFirstName !== (editDialog.first_name ?? "") ||
      editLastName !== (editDialog.last_name ?? "");

    if (roleChanged) {
      const { error } = await updateRole(editDialog.user_id, editRole);
      if (error) { toast.error("Erreur lors de la modification du rôle"); return; }
    }
    if (nameChanged) {
      const { error } = await updateProfile(editDialog.user_id, editFirstName, editLastName);
      if (error) { toast.error("Erreur lors de la modification du profil"); return; }
    }
    if (!roleChanged && !nameChanged) {
      // refetch anyway to stay consistent
      refetch();
    }
    toast.success("Administrateur mis à jour");
    setEditDialog(null);
  };

  const handleDelete = async () => {
    if (!deleteDialog) return;
    const { error } = await deleteAdmin(deleteDialog.user_id);
    if (error) { toast.error("Erreur lors de la suppression"); return; }
    toast.success("Administrateur supprimé");
    setDeleteDialog(null);
  };

  const handleInvite = async () => {
    if (!inviteEmail.trim()) return;
    setInviting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/invite-admin`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session?.access_token}`,
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
          },
          body: JSON.stringify({ email: inviteEmail.trim(), role: inviteRole, first_name: inviteFirstName.trim(), last_name: inviteLastName.trim() }),
        }
      );
      const result = await res.json();
      if (!res.ok) throw new Error(result.error || "Erreur lors de l'invitation");
      toast.success(`Invitation envoyée à ${inviteEmail}`);
      setInviteDialog(false);
      setInviteEmail("");
      setInviteFirstName("");
      setInviteLastName("");
      refetch();
    } catch (e: any) {
      toast.error(e.message || "Erreur lors de l'invitation");
    } finally {
      setInviting(false);
    }
  };

  const getDisplayName = (admin: AdminRow) => {
    const name = [admin.first_name, admin.last_name].filter(Boolean).join(" ");
    return name || null;
  };

  const getRoleBadge = (role: string) => {
    if (role === "super_admin") return <Badge variant="default">Super Admin</Badge>;
    return <Badge variant="secondary">Admin</Badge>;
  };

  return (
    <Card className="border-border/50">
      <CardHeader className="flex flex-row items-center justify-between">
        <CardTitle style={{ fontFamily: "'DM Sans', sans-serif" }}>Gestion des admins</CardTitle>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setInviteDialog(true)}>
            <Mail className="mr-2 h-4 w-4" /> Inviter
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {loading ? (
          <p className="text-muted-foreground">Chargement…</p>
        ) : admins.length === 0 ? (
          <p className="text-muted-foreground">Aucun administrateur trouvé.</p>
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {admins.map((admin) => (
                  <TableRow key={admin.user_id}>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <span>{getDisplayName(admin) || <span className="italic text-muted-foreground">Profil non renseigné</span>}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <span className="text-sm text-muted-foreground">{admin.email || "—"}</span>
                        {!admin.is_confirmed && (
                          <Badge variant="outline" className="text-xs gap-1">
                            <Clock className="h-3 w-3" />
                            Invitation envoyée
                          </Badge>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>{getRoleBadge(admin.role)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button size="icon" variant="ghost" onClick={() => handleEditOpen(admin)} title="Modifier">
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          title="Réinitialiser le mot de passe"
                          onClick={async () => {
                            if (!admin.email) { toast.error("Email manquant"); return; }
                            try {
                              const res = await fetch(
                                `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-password-email`,
                                {
                                  method: "POST",
                                  headers: { "Content-Type": "application/json" },
                                  body: JSON.stringify({ email: admin.email }),
                                }
                              );
                              const result = await res.json();
                              if (!res.ok) throw new Error(result.error || "Erreur");
                              toast.success(`Email de réinitialisation envoyé à ${admin.email}`);
                            } catch {
                              toast.error("Erreur lors de l'envoi du mail.");
                            }
                          }}
                        >
                          <KeyRound className="h-4 w-4" />
                        </Button>
                        <Button size="icon" variant="ghost" onClick={() => setDeleteDialog(admin)} title="Supprimer" className="text-destructive hover:text-destructive">
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between mt-4">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <span>Lignes par page :</span>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setPage(1); }}>
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[10, 20, 50, 100].map((s) => (
                      <SelectItem key={s} value={String(s)}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <span>{total} résultat{total > 1 ? "s" : ""}</span>
              </div>
              <div className="flex items-center gap-1">
                <Button size="icon" variant="outline" className="h-8 w-8" disabled={page <= 1} onClick={() => setPage(page - 1)}>
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <span className="text-sm px-2">{page} / {totalPages}</span>
                <Button size="icon" variant="outline" className="h-8 w-8" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onOpenChange={(o) => !o && setEditDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'administrateur</DialogTitle>
            <DialogDescription>
              {editDialog?.email || editDialog?.user_id.slice(0, 8) + "…"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom</Label>
                <Input value={editFirstName} onChange={(e) => setEditFirstName(e.target.value)} placeholder="Prénom" />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input value={editLastName} onChange={(e) => setEditLastName(e.target.value)} placeholder="Nom" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Rôle</Label>
              <Select value={editRole} onValueChange={setEditRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialog(null)}>Annuler</Button>
            <Button onClick={handleEditSave}>Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteDialog} onOpenChange={(o) => !o && setDeleteDialog(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet administrateur ?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleteDialog && (getDisplayName(deleteDialog) || deleteDialog.email || deleteDialog.user_id.slice(0, 8) + "…")} perdra tous ses accès administrateur. Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Supprimer</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Invite Dialog */}
      <Dialog open={inviteDialog} onOpenChange={setInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Inviter un administrateur</DialogTitle>
            <DialogDescription>Un email d'invitation sera envoyé à l'adresse indiquée.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" placeholder="admin@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Prénom</Label>
                <Input placeholder="Prénom" value={inviteFirstName} onChange={(e) => setInviteFirstName(e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Nom</Label>
                <Input placeholder="Nom" value={inviteLastName} onChange={(e) => setInviteLastName(e.target.value)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Rôle</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="super_admin">Super Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setInviteDialog(false)}>Annuler</Button>
            <Button onClick={handleInvite} disabled={inviting || !inviteEmail.trim()}>
              {inviting ? "Envoi…" : "Envoyer l'invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
